import React, { useEffect, useMemo, useState } from "react";
import {
	Grid,
	Typography,
	Button,
	Pagination,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	Avatar,
	Box,
	IconButton,
	TextField,
	CircularProgress
} from "@mui/material";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";

import { SubHeader } from "../../utils/componentes-utiles/SubHeader";
import { useNavigate, useLocation } from "react-router-dom";
import { getUsuariosMovil, sincronizar } from "../../services/apis";

export const Personas = () => {
	const [openRow, setOpenRow] = useState(null);

	const navigate = useNavigate();
	const location = useLocation();
	const isActive = (path) => location.pathname === path;

	const [personas, setPersonas] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState("");

	const [busqueda, setBusqueda] = useState("");

	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(25);
	const [totalPages, setTotalPages] = useState(1);

	const [syncing, setSyncing] = useState(false);
	const [syncMsg, setSyncMsg] = useState("");
	// Debounce simple (opcional, recomendado)
	const debouncedSearch = useDebouncedValue(busqueda, 350);


	const cargarPersonas = async () => {
		setLoaded(false);
		setError("");

		try {
			const response = await getUsuariosMovil({
				page,
				limit,
				search: debouncedSearch
			});

			if (response?.success === true) {
				const usuarios = response.data?.usuarios ?? [];
				const pag = response.data?.pagination ?? {};

				setPersonas(usuarios);
				setTotalPages(pag.total_pages ?? 1);
			} else {
				setPersonas([]);
				setTotalPages(1);
				setError(response?.message || "No se pudieron cargar los usuarios");
			}
		} catch (e) {
			setPersonas([]);
			setTotalPages(1);
			setError("Error de red al cargar usuarios");
		} finally {
			setLoaded(true);
		}
	};
	function toYMD(date) {
		const d = new Date(date);
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, "0");
		const day = String(d.getDate()).padStart(2, "0");
		return `${y}-${m}-${day}`;
	}
	// Si cambia búsqueda, vuelve a página 1
	useEffect(() => {
		setPage(1);
		setOpenRow(null);
	}, [debouncedSearch]);

	useEffect(() => {
		cargarPersonas();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, limit, debouncedSearch]);


	const [openSyncDialog, setOpenSyncDialog] = useState(false);
	const [syncMode, setSyncMode] = useState("hoy"); // "hoy" | "rango"

	const hoyYMD = useMemo(() => {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
	}, []);

	const [desde, setDesde] = useState(hoyYMD);
	const [hasta, setHasta] = useState(hoyYMD);

	const handleSincronizar = async (payload) => {
		setSyncing(true);
		setSyncMsg("");
		try {
			const res = await sincronizar(payload);

			if (res?.success) {
				// Resumen simple
				if (res.modo === "rango") {
					const okDays = Object.values(res.dias || {}).filter(d => d.ws_ok && !d.error).length;
					setSyncMsg(`Sincronización completada (${okDays} días procesados)`);
				} else {
					setSyncMsg(`Sincronización completada (${res?.dias?.[hoyYMD]?.ptegrupo?.ptegrupo_creados_o_actualizados ?? 0} partes)`);
				}
				await cargarPersonas();
			} else {
				setSyncMsg(res?.message || "Error al sincronizar");
			}
		} catch (e) {
			setSyncMsg("Error de red al sincronizar");
		} finally {
			setSyncing(false);
		}
	};

	const submitSync = async () => {
		if (syncMode === "rango") {
			if (!desde || !hasta) { setSyncMsg("Selecciona desde y hasta"); return; }
			if (desde > hasta) { setSyncMsg("'Desde' no puede ser mayor que 'Hasta'"); return; }
			setOpenSyncDialog(false);
			await handleSincronizar({ desde, hasta });
		} else {
			setOpenSyncDialog(false);
			await handleSincronizar({ fecha: hoyYMD });
		}
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			{/* HEADER */}
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Documentación" />
			</Grid>

			{/* TABS */}
			<Grid container spacing={2} sx={{ px: 2, pb: 2 }} alignItems="center">
				<Button variant="text" onClick={() => setOpenSyncDialog(true)} disabled={syncing}>
					<CloudSyncIcon sx={{ opacity: 0.7, animation: syncing ? "spin 1s linear infinite" : "none" }} />
					&nbsp;{syncing ? "Sincronizando..." : "Sincronizar"}
				</Button>




				<Button
					variant="text"
					onClick={() => navigate("/carpetas")}
					sx={{
						fontWeight: isActive("/carpetas") ? 600 : 400,
						borderBottom: isActive("/carpetas") ? "2px solid" : "2px solid transparent",
						borderRadius: 0
					}}
				>
					Carpetas
				</Button>

				<Button
					variant="text"
					onClick={() => navigate("/documentacion")}
					sx={{
						fontWeight: isActive("/documentacion") ? 600 : 400,
						borderBottom: isActive("/documentacion") ? "2px solid" : "2px solid transparent",
						borderRadius: 0
					}}
				>
					Documentos
				</Button>
			</Grid>

			{/* ACCIONES */}
			<Grid container justifyContent="space-between" alignItems="center" sx={{ px: 3, pb: 2 }}>
				<Grid container spacing={2} size={{ xs: "auto" }}>
					<Button size="small" variant="outlined">Documento</Button>
					<Button size="small" variant="outlined">Estado</Button>
					<Button size="small" variant="outlined">Tipo</Button>
				</Grid>

				<Grid container spacing={2} size={{ xs: "auto" }} alignItems="center">
					<TextField
						size="small"
						placeholder="Buscar por nombre..."
						value={busqueda}
						onChange={(e) => setBusqueda(e.target.value)}
					/>
					<Button variant="outlined" startIcon={<FilterAltIcon />}>Filtros</Button>
					<Button variant="outlined" startIcon={<FileDownloadIcon />}>Exportar</Button>
					<Button variant="outlined" startIcon={<ViewColumnIcon />}>Columnas</Button>
				</Grid>
			</Grid>

			{/* ESTADOS */}
			{syncMsg && (
				<Grid sx={{ px: 3, py: 1 }}>
					<Typography
						variant="body2"
						color={syncMsg.toLowerCase().includes("error") ? "error" : "success.main"}
					>
						{syncMsg}
					</Typography>
				</Grid>
			)}
			{!loaded && (
				<Grid sx={{ px: 3, py: 2 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<CircularProgress size={18} />
						<Typography variant="body2">Cargando usuarios…</Typography>
					</Box>
				</Grid>
			)}

			{loaded && error && (
				<Grid sx={{ px: 3, py: 2 }}>
					<Typography variant="body2" color="error">{error}</Typography>
				</Grid>
			)}

			{/* SCROLL MOBILE */}
			<Grid sx={{ width: "100%", overflowX: { xs: "auto", md: "visible" } }}>
				<Grid container direction="column" sx={{ minWidth: 1100 }}>
					{/* HEADER TABLA */}
					<Grid
						container
						alignItems="center"
						sx={{ px: 3, py: 1, bgcolor: "grey.100", borderBottom: "1px solid", borderColor: "divider" }}
					>
						<Grid item size={{ xs: 0.4 }} />
						<Grid item size={{ xs: 1.1 }}>Documentos</Grid>
						<Grid item size={{ xs: 2.4 }}>Nombre</Grid>
						<Grid item size={{ xs: 1.6 }}>Departamento</Grid>
						<Grid item size={{ xs: 1.1 }}>Rol</Grid>
						<Grid item size={{ xs: 1.1 }}>Teléfono</Grid>
						<Grid item size={{ xs: 1.8 }}>Email</Grid>
						<Grid item size={{ xs: 1.1 }}>NIF</Grid>
					</Grid>

					{/* FILAS REALES */}
					{loaded && !error && personas.map((u) => (
						<React.Fragment key={u.id}>
							<Grid
								container
								alignItems="center"
								sx={{
									px: 3,
									py: 1,
									borderBottom: "1px solid",
									borderColor: "divider",
									"&:hover": { bgcolor: "action.hover" }
								}}
							>
								<Grid item size={{ xs: 0.3 }}>
									<IconButton
										size="small"
										onClick={() => setOpenRow(openRow === u.id ? null : u.id)}
									>
										{openRow === u.id ? <ExpandMoreIcon /> : <ChevronRightIcon />}
									</IconButton>
								</Grid>

								<Grid item size={{ xs: 1.1 }}>
									<Box sx={{ display: "flex", gap: 0.4 }}>
										<InsertDriveFileIcon fontSize="small" color="success" />
										<InsertDriveFileIcon fontSize="small" color="warning" />
										<InsertDriveFileIcon fontSize="small" color="error" />
										<InsertDriveFileIcon fontSize="small" color="disabled" />
									</Box>
								</Grid>

								<Grid item size={{ xs: 2.4 }} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
									<Avatar src={u.imagen_url || undefined} sx={{ width: 26, height: 26 }} />
									<Typography variant="body2" noWrap>
										{u.nombre_completo || `${u.nombre ?? ""} ${u.apellidos ?? ""}`}
									</Typography>
								</Grid>

								<Grid item size={{ xs: 1.6 }}>
									<Typography variant="body2" noWrap>{u.nombre_departamento || "—"}</Typography>
								</Grid>

								<Grid item size={{ xs: 1.1 }}>
									<Chip size="small" label={u.nombre_rol || "—"} />
								</Grid>

								<Grid item size={{ xs: 1.1 }}>
									<Typography variant="body2" noWrap>{u.telefono || "—"}</Typography>
								</Grid>

								<Grid item size={{ xs: 1.8 }}>
									<Typography variant="body2" noWrap>{u.mail || "—"}</Typography>
								</Grid>

								<Grid item size={{ xs: 1.1 }}>
									<Typography variant="body2" noWrap>{u.nif || "—"}</Typography>
								</Grid>
							</Grid>

							{/* DESPLEGABLE */}
							<Collapse in={openRow === u.id}>
								<Grid container sx={{ bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}>
									<Grid container sx={{ px: 3, py: 2, overflowX: "auto" }}>
										<Typography variant="body2" sx={{ opacity: 0.7 }}>
											(Pendiente: endpoint de documentos para usuario {u.id})
										</Typography>
									</Grid>
								</Grid>
							</Collapse>
						</React.Fragment>
					))}

					{loaded && !error && personas.length === 0 && (
						<Grid sx={{ px: 3, py: 2 }}>
							<Typography variant="body2">No hay usuarios para mostrar.</Typography>
						</Grid>
					)}
				</Grid>
			</Grid>

			{/* PAGINACIÓN */}
			<Grid container justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
				<FormControl variant="standard" sx={{ minWidth: 120 }}>
					<InputLabel>Filas por página</InputLabel>
					<Select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
						<MenuItem value={25}>25</MenuItem>
						<MenuItem value={50}>50</MenuItem>
					</Select>
				</FormControl>

				<Pagination
					count={totalPages}
					page={page}
					onChange={(_, p) => { setPage(p); setOpenRow(null); }}
					color="primary"
				/>
			</Grid>
			<Dialog open={openSyncDialog} onClose={() => setOpenSyncDialog(false)} fullWidth maxWidth="xs">
				<DialogTitle>Sincronizar trabajadores</DialogTitle>
				<DialogContent>
					<RadioGroup value={syncMode} onChange={(e) => setSyncMode(e.target.value)}>
						<FormControlLabel value="hoy" control={<Radio />} label={`Solo hoy (${hoyYMD})`} />
						<FormControlLabel value="rango" control={<Radio />} label="Rango de fechas" />
					</RadioGroup>

					{syncMode === "rango" && (
						<Grid container spacing={2} sx={{ mt: 1 }}>
							<Grid item xs={6}>
								<TextField
									label="Desde"
									type="date"
									size="small"
									fullWidth
									value={desde}
									onChange={(e) => setDesde(e.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									label="Hasta"
									type="date"
									size="small"
									fullWidth
									value={hasta}
									onChange={(e) => setHasta(e.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
						</Grid>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenSyncDialog(false)} disabled={syncing}>Cancelar</Button>
					<Button variant="contained" onClick={submitSync} disabled={syncing}>Sincronizar</Button>
				</DialogActions>
			</Dialog>

		</Grid>

	);

};

// Hook local: debounce (sin librerías)
function useDebouncedValue(value, delay = 300) {
	const [debounced, setDebounced] = useState(value);

	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(t);
	}, [value, delay]);

	return debounced;
}
